import React, {forwardRef} from 'react';
import styled, {useTheme} from 'styled-components';
import {Diff} from 'utility-types';

import {RendererOf} from '../../../../core/src/helpers/react/reactHelpers';
import {VisualSizesEnum} from '../../styles/commonStyles';
import {makeSizeConstants} from '../../styles/styleHelpers';
import {ButtonColor, ButtonVariantsEnum, findButtonColors, isVariantWithBorder} from './buttonConstants';

/*
 * Props.
 */

export interface ButtonChromeProps
  extends Diff<ButtonInheritableProps, typeof defaultButtonProps>,
    Partial<typeof defaultButtonProps> {
  className?: string;
  children: RendererOf<ButtonInheritableProps>;
}

export interface ButtonInheritableProps {
  /** Size of the button. */
  size: `${VisualSizesEnum}`;
  /** Style of the button. */
  variant: `${ButtonVariantsEnum}`;
  /** Override the default color of the button. */
  color?: `${ButtonColor}`;
  /**
   * Whether the button is pill-shaped i.e. "rounded".
   * Leave unspecified unless you need to override the default.
   * @default true (false with inbox redesign enabled)
   * */
  isRounded?: boolean;
  /** Enable or disable the button. */
  isDisabled?: boolean;
  /** Whether or not the button reacts to direct hover. */
  shouldUseDirectHover?: boolean;
  /** Whether the cursor of a disabled button should be 'not-allowed'. */
  shouldDisallowCursorIfDisabled?: boolean;
}

export interface ButtonInheritableStyleProps {
  $size: `${VisualSizesEnum}`;
  $variant: `${ButtonVariantsEnum}`;
  $isRounded: boolean;
  $color?: `${ButtonColor}`;
  $isDisabled?: boolean;
  $shouldDisallowCursorIfDisabled?: boolean;
  $shouldUseDirectHover?: boolean;
  $hasIcon?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
}

/*
 * Constants.
 */

export const defaultButtonProps = {
  size: VisualSizesEnum.MEDIUM as `${VisualSizesEnum}`,
  variant: ButtonVariantsEnum.SECONDARY as `${ButtonVariantsEnum}`,
  color: ButtonColor.DEFAULT as `${ButtonColor}`,
};

/*
 * Style.
 */

const wrapperSizes = makeSizeConstants('auto', 'auto', '100%');
export const buttonChromeBorderRadiusSizes = makeSizeConstants('6px', '6px', '8px');

// We need another wrapper to avoid a Chrome position: relative bug.
const StyledWrapperDiv = styled.div`
  position: relative;
  width: fit-content;
`;

const StyledButtonDiv = styled.div<ButtonInheritableStyleProps>`
  display: flex;
  overflow: hidden;
  align-items: stretch;
  width: ${(p) => wrapperSizes[p.$size]};
  white-space: nowrap;

  border-radius: ${(p) => (p.$isRounded ? '100px' : buttonChromeBorderRadiusSizes[p.$size])};
`;

export const StyledBorderDiv = styled.div<ButtonInheritableStyleProps>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  pointer-events: none;
  border-radius: ${(p) => (p.$isRounded ? '100px' : buttonChromeBorderRadiusSizes[p.$size])};
  border: 1px solid ${(p) => findButtonColors(p.theme)[p.$variant].border};
`;

/*
 * Component.
 */

export const ButtonChrome = forwardRef<HTMLDivElement, ButtonChromeProps>(
  ({className, children, size = 'medium', variant = 'secondary', color = 'default', ...buttonProps}, ref) => {
    const theme = useTheme();
    const isRounded = buttonProps.isRounded ?? theme.rounded;

    return (
      <StyledWrapperDiv ref={ref} className={className}>
        <StyledButtonDiv
          $size={size}
          $variant={variant}
          $isRounded={isRounded}
          $color={color}
          $isDisabled={buttonProps.isDisabled}
        >
          {children({...buttonProps, size, variant, color})}
        </StyledButtonDiv>
        {maybeRenderBorder({...buttonProps, size, variant, color}, isRounded)}
      </StyledWrapperDiv>
    );
  },
);

function maybeRenderBorder(props: ButtonInheritableProps, isRounded: boolean) {
  if (!isVariantWithBorder(props.variant)) {
    return null;
  }

  return (
    <StyledBorderDiv
      $size={props.size}
      $variant={props.variant}
      $isRounded={isRounded}
      $color={props.color}
      $isDisabled={props.isDisabled}
    />
  );
}
